import { cn } from '@neo/lib/utils'
import {
  BaseEdge,
  type Edge,
  EdgeLabelRenderer,
  type EdgeProps,
  getSmoothStepPath,
} from '@xyflow/react'
import { useMemo } from 'react'
import { MenuNodeID } from '../../config/nodes.config'
import { useTypedReactFlow } from '../../hooks/use-react-flow'
import { RunType } from '../../run-info/store'
import { useFlowRunStore, useFlowStore } from '../../store'
import type { EdgeData } from '../../types'
import { isPendingEdgeHandle } from '../../utils/pending-edge'
import { EdgeLabel } from './edge-label'

type ButtonEdgeType = Edge<EdgeData>

// Helper function to create a self-loop path
const getSelfLoopPath = (sourceX: number, sourceY: number, nodeHeight = 200) => {
  // Create a circular path using arc command, positioned closer to the bottom of the node
  const centerX = sourceX
  const startY = sourceY + nodeHeight / 2 - 5 // Start closer to the node bottom
  const loopSize = 50 // Fixed radius of 50px

  // Create an SVG path using two arcs for a complete circle
  const path = `M ${centerX} ${startY - loopSize} A ${loopSize} ${loopSize} 0 0 1 ${centerX} ${startY + loopSize} A ${loopSize} ${loopSize} 0 0 1 ${centerX} ${startY - loopSize} Z`

  // Calculate label position (center of the loop)
  const labelX = centerX
  const labelY = startY // Center of the circular arc

  return [path, labelX, labelY] as const
}

export const CustomEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  source,
  sourceHandleId,
  target,
  data,
  selected,
}: EdgeProps<ButtonEdgeType>) => {
  // Check if this is a self-loop edge
  const isSelfLoop = source === target

  const sourceNode = useTypedReactFlow().getNode(source)
  const targetNode = useTypedReactFlow().getNode(target)
  const isFlowRunning = useFlowRunStore((state) => !!state.currentRunId)
  const runType = useFlowRunStore((state) => state.runType)

  // Check if there are multiple edges between the same source and target nodes
  const siblingEdgeIndex = useMemo(() => {
    const edges = useFlowStore.getState().flowInstance?.getEdges()
    if (edges && edges.length > 0) {
      const siblings = edges.filter((edge) => edge.source === source && edge.target === target)
      return siblings.findIndex((edge) => edge.sourceHandle === sourceHandleId)
    }
    return -1
  }, [source, target, sourceHandleId])

  const isConnectedToImmediatePreviousAncestorNode = useMemo(() => {
    const edges = useFlowStore.getState().flowInstance?.getEdges()
    if (!edges) return false

    // Find edges where the source node is the target (i.e., parent edges of the source node)
    const parentEdgesOfSource = edges.filter((edge) => edge.target === source)

    // Check if the target of the current edge is one of the immediate parents of the source
    return parentEdgesOfSource.some((parentEdge) => parentEdge.source === target)
  }, [source, target])

  const [edgePath, labelX, labelY] = useMemo(() => {
    if (isSelfLoop) {
      // For self-loop edges, create a circular path
      // Get actual node dimensions if available, otherwise use defaults
      const nodeHeight = sourceNode?.measured?.height || sourceNode?.height || 200
      return getSelfLoopPath(sourceX, sourceY, nodeHeight)
    }

    // This is a hack to ensure the path is not disappearing when playground is running and the edge is a straight line
    // https://stackoverflow.com/questions/73043945/why-does-a-svg-line-disappear-when-i-apply-a-svg-lineargradient
    function updatePathFinalX(path: string, offset = 0.001) {
      // Regex to match the final L command (L followed by two numbers)
      const regex = /L\s*([\d.]+)\s*([\d.]+)$/
      return path.replace(regex, (_match: string, x: string, y: string) => {
        const newX = (parseFloat(x) + offset).toFixed(9) // Add offset, limit precision
        return `L${newX} ${y}`
      })
    }
    const smoothStep = getSmoothStepPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
    })

    if (isFlowRunning || selected) smoothStep[0] = updatePathFinalX(smoothStep[0])
    // For regular edges, use the smooth step path
    return smoothStep
  }, [
    isSelfLoop,
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    sourceNode,
    isFlowRunning,
    selected,
  ])

  let newLabelY = labelY
  if (siblingEdgeIndex !== -1 && !isSelfLoop) {
    newLabelY = labelY - siblingEdgeIndex * 35
  }

  if (isConnectedToImmediatePreviousAncestorNode && sourceNode && targetNode) {
    const sourceNodeYPositionAboveTarget = sourceNode.position.y < targetNode.position.y
    if (sourceNodeYPositionAboveTarget) newLabelY -= 35
  }

  const shouldShowLabel = isPendingEdgeHandle(sourceHandleId) || sourceHandleId
  const shouldHideForMenu = isPendingEdgeHandle(sourceHandleId) && target === MenuNodeID

  const isChosenPath = data?.isChosenPath || selected

  const hasIssues = !!(
    data?.meta?.issues &&
    Array.isArray(data.meta.issues) &&
    data.meta.issues.length
  )

  // Use gradient marker for chosen path
  const chosenPathMarker = isChosenPath ? 'url(#chosen-path-marker)' : markerEnd
  const shouldFade = runType === RunType.FlowRunLogs && !isChosenPath

  return (
    <>
      <BaseEdge
        path={edgePath}
        markerEnd={chosenPathMarker}
        style={style}
        className={cn(
          sourceHandleId || '',
          data?.isChosenPath && 'edge-chosen-path opacity-100',
          selected && !data?.isChosenPath && 'edge-selected opacity-100',
          shouldFade && 'opacity-50',
          hasIssues && '!stroke-status-danger shadow-status-danger !stroke-[4px] ',
        )}
      />
      {shouldShowLabel && !shouldHideForMenu && (
        <EdgeLabelRenderer>
          <EdgeLabel
            data={data}
            id={id}
            labelX={labelX}
            labelY={newLabelY}
            source={source}
            sourceHandleId={sourceHandleId}
            target={target}
            shouldFade={shouldFade}
            selected={selected}
          />
        </EdgeLabelRenderer>
      )}
    </>
  )
}
